import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Database, GitBranch, FolderGit2, Filter, Server, Globe } from 'lucide-react';
import { Table, Select, Button } from 'antd';
import { fetchProjects } from '../../actions/infrastructures';

const MyResources = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projects } = useSelector((state) => state.infrastructures);
  const currentUser = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [resourceTypeFilter, setResourceTypeFilter] = useState('ALL');

  useEffect(() => {
    setLoading(true);
    dispatch(fetchProjects()).finally(() => setLoading(false));
  }, [dispatch]);

  const allResources = useMemo(() => {
    const resources = [];

    projects.forEach(project => {
      // Check if user is project admin
      const isProjectAdmin = currentUser?.projectPermissions?.[project.projectId]?.role === 'admin';

      // Add backend services
      if (project.backendServices) {
        project.backendServices.forEach(service => {
          resources.push({
            projectName: `${project.projectName}-${project.environment}`,
            projectId: project.projectId,
            resourceName: `${service.gitHubRepo}-${service.branchUsed}`,
            resourceId: service.serviceId,
            resourceType: 'BACKEND_SERVICE',
            displayType: 'Backend Service',
            path: `/project/${project.projectId}/backend-service/${service.serviceId}`,
            createdAt: service.createdAt,
            isProjectAdmin
          });
        });
      }

      // Add frontend apps with branches as separate resources
      if (project.frontendApps) {
        project.frontendApps.forEach(app => {
          if (app.branches) {
            app.branches.forEach(branch => {
              resources.push({
                projectName: `${project.projectName}-${project.environment}`,
                projectId: project.projectId,
                resourceName: app.appName,
                resourceId: app.appId,
                resourceType: 'FRONTEND_APP',
                displayType: 'Frontend App',
                branch: branch.branchName,
                path: `/project/${project.projectId}/frontend-app/${app.appId}`,
                createdAt: app.createdAt,
                isProjectAdmin
              });
            });
          }
        });
      }
    });

    return resources;
  }, [projects, currentUser]);

  // Filter resources the user has access to
  const accessibleResources = useMemo(() => {
    if (!currentUser) return [];
    
    return allResources.filter(resource => {
      if(currentUser?.role=== 'admin') return true;
      const projectPerms = currentUser?.projectPermissions[resource.projectId];
      if(!projectPerms) return false;
      if(projectPerms.role === 'admin') return true;
      if (!projectPerms.resources) return false;
      
      return !!projectPerms.resources[resource.resourceId];
    });
  }, [allResources, currentUser]);

  // Apply resource type filter
  const filteredResources = useMemo(() => {
    if (resourceTypeFilter === 'ALL') return accessibleResources;
    return accessibleResources.filter(resource => resource.resourceType === resourceTypeFilter);
  }, [accessibleResources, resourceTypeFilter]);

  const myResourcesColumns = [
    {
      title: 'Project Name',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text) => (
        <div className="flex items-center">
          <FolderGit2 className="h-4 w-4 mr-2 text-gray-500" />
          <span className="text-gray-900 font-medium">{text}</span>
        </div>
      ),
    },
    {
      title: 'Resource Name',
      dataIndex: 'resourceName',
      key: 'resourceName',
      render: (text, record) => (
        <div className="flex items-center">
          {record.resourceType === 'FRONTEND_APP' ? (
            <Globe className="h-4 w-4 mr-2 text-gray-500" />
          ) : (
            <Server className="h-4 w-4 mr-2 text-gray-500" />
          )}
          <span className="text-gray-900">{text}</span>
        </div>
      ),
    },
    {
      title: 'Resource Type',
      dataIndex: 'displayType',
      key: 'resourceType',
      render: (type, record) => {
        const config = {
          'BACKEND_SERVICE': {
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-800',
            borderColor: 'border-blue-200',
          },
          'FRONTEND_APP': {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            borderColor: 'border-green-200',
          }
        };

        const style = config[record.resourceType] || {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
        };
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${style.bgColor} ${style.textColor} border ${style.borderColor} inline-flex items-center justify-center w-32`}>
            {type}
          </span>
        );
      },
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      render: (branchName, record) => {
        if (record.resourceType !== 'FRONTEND_APP' || !branchName) return null;
        
        return (
          <div className="flex items-center">
            <GitBranch className="h-4 w-4 mr-2 text-gray-500" />
            <span className="text-gray-900">{branchName}</span>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          icon={<ExternalLink className="h-4 w-4 mr-1.5" />}
          onClick={() => navigate(record.path)}
          className="inline-flex items-center px-3 py-1.5 border border-indigo-500 text-indigo-600 bg-white rounded-md hover:bg-indigo-50 transition-all duration-200 shadow-sm text-sm font-medium"
        >
          Open
        </Button>
        
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="space-y-6 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">My Resources</h2>
            <p className="text-gray-500 text-sm">Discover and manage all your resources in one unified workspace</p>
          </div>
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2 text-gray-500" />
            <span className="text-gray-700 mr-2">Filter by:</span>
            <Select
              value={resourceTypeFilter}
              onChange={setResourceTypeFilter}
              className="w-40"
              options={[
                { value: 'ALL', label: 'All Resources' },
                { value: 'BACKEND_SERVICE', label: 'Backend Service' },
                { value: 'FRONTEND_APP', label: 'Frontend App' }
              ]}
            />
          </div>
        </div>

        <div className="px-6 pb-6">
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <Table
              columns={myResourcesColumns}
              dataSource={filteredResources}
              rowKey={(record) => `${record.resourceId}-${record.branch || ''}`}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} resources`,
              }}
              loading={loading}
              className="custom-table"
              locale={{ emptyText: 'You do not have access to any resources yet' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyResources;